/**
 * AdSense 광고 배너 컴포넌트.
 *
 * 광고 단위 6종 (AD_CONFIG):
 *   1. display-rect   — 디스플레이 반응형 사각형 (홈 히어로 아래, 댓글 위)
 *   2. multiplex-h    — 멀티플렉스 수평 (홈 하단, 게시판 리스트 중간, 검색 하단)
 *   3. multiplex-v    — 멀티플렉스 수직 (종목 상세 기본정보 아래)
 *   4. display-v      — 디스플레이 수직 반응형 (사이드바용 — 보류 중, enabled:false)
 *   5. in-article     — 인아티클 (게시글 본문 아래)
 *   6. fixed-300x250  — 고정형 300×250 (모바일 푸터 상단 — 슬롯 TBD, enabled:false)
 *
 * 퍼블리셔 스크립트는 `index.html <head>` 에서 1회 로드 (본 컴포넌트는 재삽입하지 않음).
 * 각 인스턴스는 mount 시 `adsbygoogle.push({})` 를 1회만 호출.
 *
 * 제외 경로 (EXCLUDED_PATH_PREFIXES):
 *   - 인증/온보딩, 폼 집중 화면, 결제·법적 페이지, 관리자, 개인 대화
 *   - 실제 App.tsx 라우트 기준으로 큐레이션됨.
 */
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { cn } from '@/lib/cn';

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    adsbygoogle?: any[];
  }
}

export type AdSlotName =
  | 'display-rect' // #1
  | 'multiplex-h' // #2
  | 'multiplex-v' // #3
  | 'display-v' // #4 — 컨테이너 우측 날개(fixed aside)
  | 'in-article' // #5
  | 'fixed-300x250' // #6 (TBD)
  | 'display-hero' // #7 — 홈 히어로 옆 2분할 반응형
  | 'display-middle'; // #8 — 홈 중단(시스템 자동 수집 ↔ 하단 grid 사이)

interface AdConfig {
  slot: string;
  /** 미지정 시 `data-ad-format` 속성 생략 — 고정 사이즈 광고는 빈 속성도 붙이지 않는 게 표준. */
  format?: string;
  layout?: string;
  fullWidth?: boolean;
  /** 고정 사이즈 — height 필수, width 는 선택 (미지정 시 inline-block 자연 폭). */
  fixedSize?: { w?: number; h: number };
  /** true 면 `<ins>` 에 inline style 을 아예 부여하지 않음. 부모 컨테이너에서 크기 제어 시 사용. */
  bare?: boolean;
  /** `<ins>` 에 직접 부여할 inline style. fixedSize/bare 보다 우선. */
  customStyle?: React.CSSProperties;
  enabled: boolean;
}

const CLIENT_ID = 'ca-pub-4221928114988868';

/* 광고 단위 레지스트리 — 슬롯 ID 변경 시 여기만 수정. */
const AD_CONFIG: Record<AdSlotName, AdConfig> = {
  'display-rect': { slot: '3110276859', format: 'auto', fullWidth: true, enabled: true },
  /* 멀티플렉스(autorelaxed) — 2026-05-29 전역 비활성화. 모바일 세로 늘어남/노필률 이슈로 display-rect 로 교체.
     슬롯 ID 는 보존 (AdSense 콘솔에서 직접 정리하거나 향후 재활성화 시 enabled:true 로 복귀). */
  'multiplex-h': { slot: '4586805433', format: 'autorelaxed', enabled: false },
  'multiplex-v': { slot: '4343697626', format: 'autorelaxed', enabled: false },
  /* #4 display-v — 컨테이너 우측 고정 날개(fixed aside), DefaultLayout 에서 xl+ 노출. */
  'display-v': { slot: '5656779295', format: 'auto', fullWidth: true, enabled: true },
  'in-article': { slot: '8082797930', format: 'fluid', layout: 'in-article', enabled: true },
  /* #6 fixed-300x250 — 슬롯 ID 확정 시 slot 값 + enabled:true 전환. */
  'fixed-300x250': { slot: 'TBD', fixedSize: { w: 300, h: 250 }, enabled: false },
  /* #7 display-hero — 홈 히어로 아래 고정 디스플레이 (AdSense 콘솔 재설정: 350×120 고정).
     format/fullWidth 속성 제거 — 고정 사이즈는 style 의 inline-block + w/h 만으로 판정. */
  'display-hero': { slot: '2888078909', fixedSize: { w: 350, h: 120 }, enabled: true },
  /* #8 display-middle — 홈 중단(시스템 자동 수집 ↔ 하단 grid 사이).
     AdSense 콘솔 타입 미확인 — 반응형 디스플레이(auto+fullWidth)로 기본 등록. */
  'display-middle': { slot: '4382391924', format: 'auto', fullWidth: true, enabled: true },
};

/**
 * 광고 미노출 경로 prefix 목록 — 실제 App.tsx 라우트 기준.
 *
 * AdSense 정책 및 UX 기준:
 *   - 인증/온보딩:        /auth/callback, /onboarding
 *   - 편집·폼 집중 화면:  /write, /me/edit, /settings, /diary/new
 *   - 결제/정산:          /exchange, /premium/success, /premium/fail
 *   - 법적/정책 페이지:   /terms, /privacy, /disclaimer
 *   - 관리자:             /admin
 *   - 개인 대화:          /messages
 *
 * `/exchange/history` / `/points/history` / `/diary` (리스트) 등 정보성 화면은 허용.
 */
const EXCLUDED_PATH_PREFIXES = [
  '/auth/callback',
  '/onboarding',
  '/write',
  '/me/edit',
  '/settings',
  '/diary/new',
  '/exchange', // /exchange, /exchange/history 둘 다 커버 — 결제 컨텍스트라 전구간 제외
  '/premium/success',
  '/premium/fail',
  '/terms',
  '/privacy',
  '/disclaimer',
  '/admin',
  '/messages',
] as const;

function isPathExcluded(pathname: string): boolean {
  return EXCLUDED_PATH_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

/* ══════════════════════════════════════════════════════════════
 *  Wing(날개) 광고 — 라우트별 슬롯 오버라이드 레지스트리
 *
 *  DefaultLayout 의 우측 고정 날개 광고를 페이지별로 다르게 노출하고 싶을 때 사용.
 *  AdSense 콘솔에서 슬롯을 새로 만들고 → AD_CONFIG 에 신규 AdSlotName 으로 등록 →
 *  아래 WING_AD_ROUTE_MAP 에 `{ prefix, slot }` 한 줄 추가하면 끝.
 *
 *  규칙:
 *   - 배열 순서대로 탐색하며 첫 매치 사용 → 더 구체적인 prefix 를 위쪽에 배치
 *     (예: '/board/stock' 을 '/board' 보다 먼저)
 *   - prefix 매치 방식: 정확 일치 또는 `${prefix}/` 접두. 파라미터 경로(/ticker/005930) 도 커버.
 *   - 매치 안 되는 모든 경로 → WING_AD_DEFAULT ('display-v') 로 폴백
 *
 *  확장 예시 (AdSense 슬롯 ID 확보 후 주석 해제):
 *    // { prefix: '/ticker',       slot: 'wing-ticker'  as AdSlotName },
 *    // { prefix: '/board/stock',  slot: 'wing-stock'   as AdSlotName },
 *    // { prefix: '/events',       slot: 'wing-events'  as AdSlotName },
 * ══════════════════════════════════════════════════════════════ */

export const WING_AD_DEFAULT: AdSlotName = 'display-v';

export const WING_AD_ROUTE_MAP: ReadonlyArray<{ prefix: string; slot: AdSlotName }> = [
  /* 페이지별 날개 광고 슬롯 — 위쪽이 더 구체적. 비어있으면 전역 display-v 사용. */
];

export function resolveWingAdSlot(pathname: string): AdSlotName {
  const match = WING_AD_ROUTE_MAP.find(
    (r) => pathname === r.prefix || pathname.startsWith(`${r.prefix}/`),
  );
  return match?.slot ?? WING_AD_DEFAULT;
}

interface AdBannerProps {
  slot: AdSlotName;
  className?: string;
  /** true 면 EXCLUDED_PATH_PREFIXES 무시 — 전역 레이아웃(날개광고 등) 에서 사용. */
  ignoreExcluded?: boolean;
}

export function AdBanner({ slot, className, ignoreExcluded = false }: AdBannerProps) {
  const { pathname } = useLocation();
  const pushed = useRef(false);
  const config = AD_CONFIG[slot];
  const excluded = !ignoreExcluded && isPathExcluded(pathname);

  useEffect(() => {
    if (!config.enabled) return;
    if (excluded) return;
    if (pushed.current) return;

    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
      pushed.current = true;
    } catch (err) {
      /* AdSense 미로드 상태에서 push 호출 시 발생. 다음 마운트에서 재시도. */
      console.warn('[AdBanner] adsbygoogle.push 실패', err);
    }
    /* pathname 변경 시 새 인스턴스가 마운트되므로 재시도 불필요. */
  }, [pathname, config.enabled, excluded]);

  if (!config.enabled) return null;
  if (excluded) return null;

  const style: React.CSSProperties | undefined = config.customStyle
    ? config.customStyle
    : config.bare
    ? undefined
    : config.fixedSize
    ? {
        display: 'inline-block',
        /* width 는 지정된 경우에만 — AdSense 가 height 고정 + 자유 폭 배너 선택할 수 있게. */
        ...(config.fixedSize.w !== undefined ? { width: `${config.fixedSize.w}px` } : {}),
        height: `${config.fixedSize.h}px`,
      }
    : {
        display: 'block',
        textAlign: 'center',
      };

  const extraProps: Record<string, string> = {};
  /* AdSense 고정 사이즈 광고는 data-ad-format 속성 자체를 부여하지 않음 —
     format 미지정 시 spread 에서 생략해 원본 AdSense 마크업과 동일하게 유지. */
  if (config.format) extraProps['data-ad-format'] = config.format;
  if (config.layout) extraProps['data-ad-layout'] = config.layout;
  if (config.fullWidth) extraProps['data-full-width-responsive'] = 'true';

  return (
    <div className={cn('ad-slot', className)} aria-label="광고">
      <ins
        className="adsbygoogle"
        {...(style ? { style } : {})}
        data-ad-client={CLIENT_ID}
        data-ad-slot={config.slot}
        {...extraProps}
      />
    </div>
  );
}

export default AdBanner;
